// =====================================================
// 🖼️ COMPONENTE: PartImages - Renderizar imágenes de partes
// =====================================================
// Agregar después de las interfaces (línea ~90)

interface PartImagesProps {
  images: Array<{
    filename: string;
    path: string;
    page?: number;
  }>;
  isDark: boolean;
}

const PartImages: React.FC<PartImagesProps> = ({ images, isDark }) => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());

  if (!images || images.length === 0) return null;

  const handleImageError = (index: number) => {
    setImageErrors(prev => new Set(prev).add(index));
  };

  const validImages = images.filter((_, idx) => !imageErrors.has(idx));

  if (validImages.length === 0) return null;

  return (
    <div className="mt-4 space-y-2">
      {/* Título */}
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
        <FileText className="w-4 h-4" />
        <span>
          {validImages.length === 1 
            ? 'Imagen de referencia' 
            : `${validImages.length} imágenes de referencia`}
        </span>
      </div>

      {/* Grid de imágenes */}
      <div className={`grid gap-3 ${
        validImages.length === 1 ? 'grid-cols-1' : 
        validImages.length === 2 ? 'grid-cols-2' : 
        'grid-cols-2 sm:grid-cols-3'
      }`}>
        {validImages.map((img, idx) => (
          <div
            key={idx}
            className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
              isDark 
                ? 'border-gray-700 hover:border-yellow-500 bg-gray-800' 
                : 'border-gray-300 hover:border-yellow-600 bg-white'
            }`}
            onClick={() => setSelectedImage(idx)}
          >
            {/* Imagen */}
            <img
              src={`${import.meta.env.VITE_API_URL || ''}/api/document-images${img.path}`}
              alt={img.filename}
              className="w-full h-32 object-contain p-2"
              onError={() => handleImageError(idx)}
            />

            {/* Overlay con info */}
            <div className={`absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2`}>
              <div className="text-white text-xs">
                {img.page && <div>Página {img.page}</div>}
                <div className="truncate">{img.filename}</div>
              </div>
            </div>

            {/* Ícono de zoom */}
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-yellow-500 rounded-full p-1">
                <Search className="w-3 h-3 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de imagen ampliada */}
      {selectedImage !== null && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh] w-full">
            {/* Botón cerrar */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-yellow-400 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>

            {/* Imagen ampliada */}
            <img
              src={`${import.meta.env.VITE_API_URL || ''}/api/document-images${validImages[selectedImage].path}`}
              alt={validImages[selectedImage].filename}
              className="w-full h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Info de imagen */}
            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-4">
              <div className="text-sm">
                {validImages[selectedImage].page && (
                  <div>Página {validImages[selectedImage].page}</div>
                )}
                <div className="font-medium">{validImages[selectedImage].filename}</div>
              </div>

              {/* Navegación entre imágenes */}
              {validImages.length > 1 && (
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage(prev => 
                        prev === null ? 0 : (prev - 1 + validImages.length) % validImages.length
                      );
                    }}
                    className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded transition-colors text-sm"
                  >
                    ← Anterior
                  </button>
                  <span className="px-3 py-1 bg-white/10 rounded text-sm">
                    {selectedImage + 1} / {validImages.length}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage(prev => 
                        prev === null ? 0 : (prev + 1) % validImages.length
                      );
                    }}
                    className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded transition-colors text-sm"
                  >
                    Siguiente →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartImages;